<template>
  <div class="map-component">
    <yandex-map
      :settings="settings"
      :coords="mapCenter"
      :zoom="mapZoom"
      :cluster-options="clusterOptions"
      :controls="['zoomControl']"
      @map-was-initialized="onMapInit"
    >
      <!--      :controls="settings.controls"-->
      <filial-marker v-if="filialCoords" :filial="filial" />

      <map-polygon
        v-for="p in polygons"
        :key="`polygon-${p.id}`"
        :polygon="p"
      />

      <order-marker
        v-for="order in computedOrders"
        :key="`order-${order.code}-${getTimestamp()}`"
        :order="order"
      />

      <courier-marker
        v-for="courier in computedCouriers"
        :courier="courier"
        :key="`courier-${courier.id}-${getTimestamp()}`"
      />

      <slot />
    </yandex-map>
  </div>
</template>

<script>
  import yandexMap from 'vue-yandex-maps'
  import CourierMarker from './СourierMarker'
  import OrderMarker from './OrderMarker.vue'
  import MapPolygon from './MapPolygon.vue'
  import FilialMarker from './FilialMarker.vue'
  import { DateTime } from 'luxon'
  import { mapState } from 'vuex'

  export default {
    name: 'MapBlock',
    components: {
      MapPolygon,
      OrderMarker,
      CourierMarker,
      FilialMarker,
      yandexMap,
    },
    props: {
      polygons: {
        type: Array,
        default: () => ([]),
      },
      filial: {
        type: Object,
        default: () => ({}),
      },
      orders: {
        type: Array,
        default: () => ([]),
      },
      couriers: {
        type: Array,
        default: () => ([]),
      },
      center: {
        type: String,
        default: null,
      },
      zoom: {
        type: [String, Number],
        default: 11,
      },
      showCouriers: Boolean,
    },
    data () {
      return {
        clusterOptions: {
          1: {
            clusterDisableClickZoom: true,
            clusterOpenBalloonOnClick: true,
            clusterBalloonLayout: [
              '<ul class="list">',
              '{% for geoObject in properties.geoObjects %}',
              '<li>{{ geoObject.properties.balloonContentHeader|raw }}</li>',
              '{% endfor %}',
              '</ul>',
            ].join(''),
          },
        },
        defaultCenter: [55.762832, 37.611520],
      }
    },
    computed: {
      ...mapState({
        apiKey: state => state.app.yandexMapsYandexMapsApiKey,
      }),
      settings () {
        return {
          apiKey: this.apiKey,
          lang: 'ru_RU',
          coordorder: 'latlong',
          version: '2.1',
          controls: ['zoomControl'],
        }
      },
      mapZoom () {
        return this.zoom || 11
      },
      mapCenter () {
        if (this.center) {
          return this.center
            .split(',')
            .map(v => parseFloat(v.trim()))
        }
        return this.defaultCenter
      },
      computedOrders () {
        return this.orders ? this.orders.filter(order => order.deliveryType === 'delivery') : []
      },
      computedCouriers () {
        return this.couriers && this.showCouriers ? this.couriers : []
      },
      filialCoords () {
        const lat = parseFloat(this.filial && this.filial.lat)
        const lng = parseFloat(this.filial && this.filial.let)
        if (Number.isNaN(lat) || Number.isNaN(lng)) return null
        return [lat, lng]
      },
      polygonsBounds () {
        let minLat = Infinity
        let minLng = Infinity
        let maxLat = -Infinity
        let maxLng = -Infinity
        this.polygons.forEach(polygon => {
          const rings = (polygon && polygon.area && polygon.area.coordinates) || []
          rings.forEach(ring => {
            (ring || []).forEach(point => {
              const lng = parseFloat(point && point[0])
              const lat = parseFloat(point && point[1])
              if (Number.isNaN(lat) || Number.isNaN(lng)) return
              minLat = Math.min(minLat, lat)
              maxLat = Math.max(maxLat, lat)
              minLng = Math.min(minLng, lng)
              maxLng = Math.max(maxLng, lng)
            })
          })
        })
        if (minLat === Infinity) return null
        return [[minLat, minLng], [maxLat, maxLng]]
      },
    },
    methods: {
      getTimestamp () {
        return DateTime.now().toMillis()
      },
      onMapInit (map) {
        this.mapInstance = map
        this.fitMapToPolygons()
      },
      fitMapToPolygons () {
        if (!this.mapInstance) return
        if (this.polygonsBounds) {
          this.mapInstance.setBounds(this.polygonsBounds, {
            checkZoomRange: true,
            zoomMargin: 32,
          })
        } else if (this.filialCoords) {
          this.mapInstance.setCenter(this.filialCoords)
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
.map-component {
  width: 100%;
  height: 100%;
  position: relative;
  //z-index: 1;

  .ymap-container {
    width: 100%;
    height: 100%;
  }
  ::v-deep {
    // скругление стандартных балунов Яндекс.Карт только на этой карте;
    // классы содержат версию API (ymaps-2-1-79-...), поэтому матчим по подстроке
    [class*='-balloon_layout_normal'],
    [class*='-balloon__layout'],
    [class*='-balloon__content'] {
      border-radius: 10px;
    }

    [class*='-balloon__layout'] {
      overflow: hidden;
    }

    .balloon {
      min-width: 300px;
      min-height: 150px;
      padding: 15px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-around;
      color: black;
      line-height: 1.3;
      font-size: 15px;
      text-align: center;
      p {
        margin-bottom: 5px;
      }
      &__info {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        width: 100%;
      }

      &__id {
        margin-bottom: 10px;
        font-size: 12px;
        color: #ccc;
      }

      &__code {
        font-weight: bold;
      }

      &__status {
        font-size: 14px;
      }

      &__courier {
        font-size: 14px;
      }

      &__adress {
        font-size: 14px;
      }
    }
  }
}
</style>
